import api from './api';

export interface NewsPreviewSettings {
  percentage: number;
  enabled: boolean;
}

const DEFAULT_SETTINGS: NewsPreviewSettings = {
  percentage: 50,
  enabled: true,
};

class NewsPreviewService {
  private cachedSettings: NewsPreviewSettings | null = null;
  private inFlight: Promise<NewsPreviewSettings> | null = null;

  async getSettings(): Promise<NewsPreviewSettings> {
    if (this.cachedSettings) return this.cachedSettings;
    if (this.inFlight) return this.inFlight;

    this.inFlight = (async () => {
      try {
        const response = await api.get('/api/settings/news-preview');
        const raw = response?.data;
        if (raw?.status === 'success' && raw?.data?.newsPreview) {
          const np = raw.data.newsPreview;
          const parsed: NewsPreviewSettings = {
            percentage:
              typeof np.percentage === 'number' && !isNaN(np.percentage)
                ? Math.max(0, Math.min(100, np.percentage))
                : DEFAULT_SETTINGS.percentage,
            enabled: typeof np.enabled === 'boolean' ? np.enabled : DEFAULT_SETTINGS.enabled,
          };
          this.cachedSettings = parsed;
          return parsed;
        }
      } catch (error) {
        console.error('[NewsPreviewService] Error fetching settings:', error);
      }
      this.cachedSettings = DEFAULT_SETTINGS;
      return DEFAULT_SETTINGS;
    })();

    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  clearCache(): void {
    this.cachedSettings = null;
  }

  /** Kürzt Plain-Text-Content anhand percentage (0-100).
   *  Bricht nicht mitten im Wort ab (sucht rückwärts nach Space/Newline). */
  truncateContent(content: string, settings: NewsPreviewSettings): string {
    if (!content) return content;
    if (!settings.enabled) return content;
    const percentage = settings.percentage;
    if (percentage >= 100) return content;
    if (percentage <= 0) return '';

    const targetLength = Math.round((content.length * percentage) / 100);
    if (targetLength >= content.length) return content;

    let cutIndex = targetLength;
    while (
      cutIndex > 0 &&
      content[cutIndex] !== ' ' &&
      content[cutIndex] !== '\n'
    ) {
      cutIndex--;
    }
    if (cutIndex === 0) cutIndex = targetLength;

    return content.substring(0, cutIndex);
  }

  /** Kürzt HTML-Content: verwendet Plain-Text-Länge als Richtwert,
   *  schneidet sauber vor dem nächsten Element-Ende ab und schließt offene Tags. */
  truncateHtml(html: string, settings: NewsPreviewSettings): string {
    if (!html) return html;
    if (!settings.enabled) return html;
    const percentage = settings.percentage;
    if (percentage >= 100) return html;
    if (percentage <= 0) return '';

    const plainText = html.replace(/<[^>]*>/g, '');
    const targetPlainLength = Math.round(
      (plainText.length * percentage) / 100
    );
    if (targetPlainLength <= 0) return '';

    let plainCount = 0;
    let i = 0;
    const openTags: string[] = [];

    while (i < html.length && plainCount < targetPlainLength) {
      const ch = html[i];
      if (ch === '<') {
        const close = html.indexOf('>', i);
        if (close === -1) break;
        const tag = html.substring(i, close + 1);
        const isClosing = tag.startsWith('</');
        const isSelfClosing = tag.endsWith('/>') || /^<(br|img|hr|meta|input|link)\b/i.test(tag);
        if (!isClosing && !isSelfClosing) {
          const match = tag.match(/^<\s*([a-zA-Z0-9]+)/);
          if (match) openTags.push(match[1].toLowerCase());
        } else if (isClosing) {
          const match = tag.match(/^<\/\s*([a-zA-Z0-9]+)/);
          if (match) {
            const tagName = match[1].toLowerCase();
            const idx = openTags.lastIndexOf(tagName);
            if (idx !== -1) openTags.splice(idx, 1);
          }
        }
        i = close + 1;
      } else {
        plainCount++;
        i++;
      }
    }

    let truncated = html.substring(0, i);
    for (let k = openTags.length - 1; k >= 0; k--) {
      truncated += `</${openTags[k]}>`;
    }
    return truncated;
  }

  isContentTruncated(content: string | undefined | null, settings: NewsPreviewSettings): boolean {
    if (!content || content.trim().length === 0) return false;
    if (!settings.enabled) return false;
    return settings.percentage < 100;
  }
}

export default new NewsPreviewService();
